import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useDeadLetterDetail } from '../../application/hooks/useDeadLetterMessages';
import { useAdminPreferences } from '@/contexts/identityaccess/application/hooks/useAdminPreferences';
import { formatDate } from '@/shared/utils/dateUtils';

interface DeadLetterDetailModalProps {
  messageId: number | null;
  open: boolean;
  onClose: () => void;
  onRetry?: (id: number) => void;
  onRemove?: (id: number) => void;
}

export const DeadLetterDetailModal: React.FC<DeadLetterDetailModalProps> = ({
  messageId,
  open,
  onClose,
  onRetry,
  onRemove,
}) => {
  const { data: detail, isLoading, error } = useDeadLetterDetail(open ? messageId : null);
  const { language, timezone } = useAdminPreferences();

  const handleRetry = () => {
    if (messageId && onRetry) {
      onRetry(messageId);
      onClose();
    }
  };

  const handleRemove = () => {
    if (messageId && onRemove) {
      onRemove(messageId);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="dead-letter-detail-title"
    >
      <DialogTitle id="dead-letter-detail-title">
        Dead Letter Message Detail #{messageId}
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error || !detail ? (
          <Typography color="error">
            {error instanceof Error ? error.message : 'Failed to load message detail'}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Transport
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={detail.transport} size="small" variant="outlined" />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Failed at
                </Typography>
                <Typography variant="body2">
                  {formatDate(detail.failedAt, { language, timezone, includeTime: true })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Attempts
                </Typography>
                <Typography variant="body2">{detail.history.length + 1}</Typography>
              </Box>
            </Box>

            {/* Redelivery history */}
            {detail.history.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Redelivery history
                  </Typography>
                  <List dense disablePadding>
                    {detail.history.map((ts, i) => (
                      <ListItem key={i} disablePadding sx={{ py: 0 }}>
                        <ListItemText
                          primary={formatDate(ts, { language, timezone, includeTime: true })}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontFamily: "'Fira Code', monospace",
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </>
            )}

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Exception
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                {detail.errorClass}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {detail.error}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Message Payload
              </Typography>
              <TextField
                value={detail.messageContent}
                multiline
                fullWidth
                rows={12}
                slotProps={{
                  input: {
                    readOnly: true,
                    style: { fontFamily: "'Fira Code', monospace", fontSize: '0.78rem' },
                  },
                }}
                variant="outlined"
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {onRemove && (
          <Button onClick={handleRemove} color="error">
            Remove
          </Button>
        )}
        {onRetry && (
          <Button onClick={handleRetry} color="primary" variant="contained">
            Retry Message
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
